import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useSnackbar } from 'notistack';
import { progressFlgState } from '../../GlobalState';
import {
  useStyles,
  ModalSubmitButton,
  ModalInnerButton
} from '../parts/cloudStorage/style/style';
import {
  getPictureDownload,
  editPictureDetail
} from '../../apicalls/cloud/sharePicture';
import { Grid, TextField } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import {
  checkResData,
  formatDatetime,
  formatDataSize
} from '../../utils/common';
import type { TSharedPicture } from '../../types/cloudStorage/CloudFileList';

type TProps = {
  picture: TSharedPicture;
  getSharedPictureList: () => void;
  setDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPictures: React.Dispatch<React.SetStateAction<TSharedPicture[]>>;
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isGuestPicture: boolean;
};

// 写真詳細表示
export const PictureDetailField: React.FC<TProps> = (props: TProps) => {
  const {
    picture,
    getSharedPictureList,
    setDetailModalOpen,
    setSelectedPictures,
    setDeleteModalOpen,
    isGuestPicture
  } = props;

  const classes = useStyles();

  // メッセージ
  const { enqueueSnackbar } = useSnackbar();

  const [src, setSrc] = useState<string>('');
  const [, setProgressFlg] = useRecoilState<boolean>(progressFlgState);

  // 写真詳細編集用
  const [pictureShootingDate, setPictureShootingDate] = useState<string>(
    picture.shooting_date
  );
  const [pictureDescription, setPictureDescription] = useState<string>(
    picture.description
  );

  // 写真ダウンロード
  const onDownload = () => {
    setProgressFlg(true);

    getPictureDownload(String(picture.id), true).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        let bin = atob(data.data.replace(/^.*,/, ''));

        let buffer = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
          buffer[i] = bin.charCodeAt(i);
        }

        let image_file = new File([buffer.buffer], data.data, {
          type: data.file_format
        });

        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(image_file);
        const fileName = picture.file_name;
        downloadLink.download = fileName;
        downloadLink.click();
      });
      setProgressFlg(false);
    });
  };

  // 写真更新
  const editPicture = () => {
    editPictureDetail(
      picture.id,
      picture.construct_id,
      picture.picture_folder_id,
      pictureShootingDate,
      pictureDescription
    ).then(resData => {
      checkResData(resData, enqueueSnackbar).then(() => {
        getSharedPictureList();
        setDetailModalOpen(false);
      });
    });
  };

  // この詳細モーダルで表示している写真１枚を削除するために、selectedPicturesに追加
  const addSelectedPictures = () => {
    setSelectedPictures([]); // リセット
    setSelectedPictures([picture]);
  };

  // ページ開いた時に実行
  useEffect(() => {
    setProgressFlg(true);

    // 写真表示のためのダウンロード
    getPictureDownload(String(picture.id), false).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        const imageData = data.data_for_display ?? data.data;
        const fileFormat = data.file_format_for_display ?? data.file_format;
        setSrc(`data:${fileFormat};base64,${imageData.replace(/^.*,/, '')}`);
      });
      setProgressFlg(false);
    });
  }, [picture.id]);

  return (
    <>
      <form className={classes.paper}>
        <Grid container>
          {/* クローズボタン */}
          <Grid
            item
            xs={12}
            style={{ display: 'flex', justifyContent: 'end', padding: 20 }}
          >
            <CloseIcon
              style={{ fontSize: 36, cursor: 'pointer' }}
              onClick={() => {
                setDetailModalOpen(false);
              }}
            />
          </Grid>

          {/* モーダル左側 */}
          <Grid item xs={9} className={classes.pictureArea}>
            <img
              src={src}
              alt='取得画像'
              style={{
                height: 320,
                width: '100%',
                objectFit: 'contain'
              }}
            />
          </Grid>

          {/* モーダル右側 */}
          <Grid item xs={3} className={classes.modalRight}>
            <div>
              <span style={{ marginRight: 12, marginBottom: 8 }}>フォルダ</span>
              {picture.picture_folder_name}
            </div>
            <div>
              <span style={{ marginRight: 12, marginBottom: 8 }}>作成日</span>
              {formatDatetime(
                new Date(picture.created_at),
                'YYYY.MM.DD',
                '日本標準時'
              )}
            </div>
            <div>
              <span style={{ marginRight: 12 }}>作成者</span>
              {picture.created_name}
            </div>
            <div>
              <span style={{ marginRight: 12 }}>サイズ</span>
              {formatDataSize(picture.size)}
            </div>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <ModalInnerButton onClick={onDownload}>
                <GetAppIcon />
                ダウンロード
              </ModalInnerButton>

              {isGuestPicture && (
                <p
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => {
                    addSelectedPictures();
                    setDeleteModalOpen(true);
                  }}
                >
                  削除する
                </p>
              )}
            </div>
          </Grid>

          {/* 入力 or 表示フォーム */}
          {/* 共有先がアップロードしたファイルの場合（isGuestPicture=true）は編集可、他のユーザーのファイルは編集不可 */}
          <Grid item xs={12} style={{ padding: 24 }}>
            <table>
              <tr>
                <th className={classes.tableHead}>撮影日</th>
                <td>
                  {isGuestPicture ? (
                    <TextField
                      variant='outlined'
                      type='date'
                      value={pictureShootingDate}
                      onChange={e => {
                        setPictureShootingDate(e.target.value);
                      }}
                    />
                  ) : (
                    <p style={{ margin: 0 }}>
                      {formatDatetime(
                        new Date(pictureShootingDate),
                        'YYYY年MM月DD日'
                      )}
                    </p>
                  )}
                </td>
              </tr>
              <tr>
                <th className={classes.tableHead}>説明</th>
                <td>
                  {isGuestPicture ? (
                    <TextField
                      variant='outlined'
                      style={{ minWidth: 600 }}
                      value={pictureDescription}
                      rows={3}
                      multiline={true}
                      onChange={e => {
                        setPictureDescription(e.target.value);
                      }}
                    />
                  ) : (
                    <p style={{ margin: 0 }}>{pictureDescription}</p>
                  )}
                </td>
              </tr>
            </table>
          </Grid>

          {/* モーダルフッター */}
          <Grid item xs={12} className={classes.modalFooter}>
            {isGuestPicture ? (
              <ModalSubmitButton variant='contained' onClick={editPicture}>
                更新する
              </ModalSubmitButton>
            ) : (
              <ModalSubmitButton
                variant='contained'
                onClick={() => setDetailModalOpen(false)}
              >
                閉じる
              </ModalSubmitButton>
            )}
          </Grid>
        </Grid>
      </form>
    </>
  );
};
